import { select, withSelect } from "@wordpress/data";
import React from "react";

type BreadcrumbItem = {
	label: string;
	link: string;
};

type BreadcrumbsProps = {
	content?: string;
	separator?: string;
	postType?: string;
	postId?: number;
	postTitle?: string;
	isTyping?: boolean;
	order?: string[];
	showHomeLink?: boolean;
	spacing?: number;
	homeLabel?: string;
};

const generateDynamicBreadcrumbs = (
	props: BreadcrumbsProps
): BreadcrumbItem[] => {
	const { postType, postId, postTitle, showHomeLink, homeLabel } = props;
	const breadcrumbs: BreadcrumbItem[] = showHomeLink
		? [{ label: homeLabel || "Home", link: "/" }]
		: [];

	switch (postType) {
		case "post":
			try {
				const categories = select("core")?.getEntityRecords(
					"taxonomy",
					"category",
					{ per_page: 1, post: postId }
				);
				if (categories && categories.length > 0) {
					breadcrumbs.push({
						label: categories[0].name,
						link: categories[0].link,
					});
				}
			} catch (error) {
				console.error("Error fetching categories:", error);
			}
			break;

		case "page":
			try {
				let currentPage = select("core/editor")?.getCurrentPost();
				while (currentPage?.parent) {
					const parentPage = select("core")?.getEntityRecords(
						"postType",
						"page",
						{
							include: [currentPage.parent],
							per_page: 1,
						}
					);

					if (parentPage && parentPage.length > 0) {
						breadcrumbs.unshift({
							label: parentPage[0].title.rendered,
							link: parentPage[0].link,
						});
						currentPage = parentPage[0];
					} else {
						break;
					}
				}
			} catch (error) {
				console.error("Error fetching parent pages:", error);
			}
			break;

		case "product":
			try {
				const shopPage = select("core")?.getEntityRecords(
					"postType",
					"page",
					{
						slug: "shop",
						per_page: 1,
					}
				);

				if (shopPage && shopPage.length > 0) {
					breadcrumbs.push({
						label: shopPage[0].title.rendered,
						link: shopPage[0].link,
					});
				}
			} catch (error) {
				console.error("Error fetching shop page:", error);
			}
			break;

		case "archive":
			try {
				const archivePage = select("core")?.getEntityRecords(
					"postType",
					postType,
					{ per_page: 1 }
				);

				if (archivePage && archivePage.length > 0) {
					breadcrumbs.push({
						label: archivePage[0].title.rendered,
						link: archivePage[0].link,
					});
				}
			} catch (error) {
				console.error("Error fetching archive page:", error);
			}
			break;
		case "search":
			try {
				const searchPage = select("core")?.getEntityRecords(
					"postType",
					"page",
					{ slug: "search", per_page: 1 }
				);

				if (searchPage && searchPage.length > 0) {
					breadcrumbs.push({
						label: searchPage[0].title.rendered,
						link: searchPage[0].link,
					});
				}
			} catch (error) {
				console.error("Error fetching search page:", error);
			}
	}

	if (postTitle) {
		breadcrumbs.push({
			label: postTitle,
			link: "",
		});
	}

	return breadcrumbs;
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
	content,
	separator,
	showHomeLink,
	homeLabel,
	spacing,
	postType,
	postId,
	postTitle,
	isTyping,
	order,
}) => {
	const breadcrumbs = generateDynamicBreadcrumbs({
		content,
		postType,
		postId,
		postTitle,
		isTyping,
		order,
		showHomeLink,
		homeLabel,
		spacing,
	});

	const containerStyle: React.CSSProperties = {
		display: "flex",
		alignItems: "center",
		margin: "10px 0",
	};

	const linkStyle: React.CSSProperties = {
		textDecoration: "none",
		transition: "color 0.3s ease",
	};

	return (
		<nav aria-label="Breadcrumbs" className="mzb-breadcrumb">
			<div style={containerStyle}>
				{breadcrumbs.map((item, index) => (
					<React.Fragment key={index}>
						{index > 0 && (
							<span className="mzb-breadcrumb-separator">
								{separator}
							</span>
						)}
						<a
							href={item.link}
							style={linkStyle}
							onClick={(e) =>
								item.link === "" ? e.preventDefault() : null
							}
							className="mzb-breadcrumb-link"
						>
							{item.label}
						</a>
					</React.Fragment>
				))}
			</div>
		</nav>
	);
};

//@ts-ignore
export default withSelect((select) => {
	const { getEditedPostContent, getCurrentPost, getCurrentPostType } =
		select("core/editor");
	const { getBlockOrder, isTyping } = select("core/block-editor");

	const currentPost = getCurrentPost();

	return {
		content: getEditedPostContent(),
		postType: getCurrentPostType(),
		postId: currentPost.id,
		postTitle: currentPost.title,
		isTyping: isTyping(),
		order: getBlockOrder(),
	};
})(Breadcrumbs);
